import { GQLProductSideFragment } from '@theprintbar/graphql';
import { getArtboardWidthAndHeight, invariant, mmToPixels, percentageOf } from '../..';
import { Position } from './schema';

export function getBackgroundDimensionInMM(side: GQLProductSideFragment) {
	const { width } = getArtboardWidthAndHeight(side);

	return width / (1 - (100 - side.imageWidth) / 100);
}

export function getBackgroundDimensionInPixels(side: GQLProductSideFragment, dpi = 300) {
	return mmToPixels(getBackgroundDimensionInMM(side), dpi);
}

export function positionToPercentageString(percentages: Position) {
	return {
		top: `${percentages.top}%`,
		left: `${percentages.left}%`,
		centreOffset: `${percentages.centreOffset}%`,
		width: `${percentages.width}%`,
		height: `${percentages.height}%`,
	};
}

export function getPrintAreaHeightAsPercentage(side: GQLProductSideFragment) {
	const { width, height } = getArtboardWidthAndHeight(side);
	return side.imageWidth * (height / width);
}

export function getArtboardPositionAsPercentage(
	side: Pick<GQLProductSideFragment, 'printWidth' | 'printHeight' | 'imageWidth' | 'imageOffsetTop' | 'imageOffsetLeft'>,
) {
	const { width, height } = getArtboardWidthAndHeight(side);

	return {
		top: side.imageOffsetTop,
		left: side.imageOffsetLeft,
		centreOffset: 0,
		width: side.imageWidth,
		height: side.imageWidth * (height / width),
	};
}

export function getPrintAreaPositionAsPercentageString(side: GQLProductSideFragment) {
	return positionToPercentageString(getArtboardPositionAsPercentage(side));
}

export function getDecorationPositionAsPercentage(decoration: { side?: GQLProductSideFragment | null } & Position): Position {
	invariant(decoration.side, 'Decoration side is missing');
	const { width, height } = getArtboardWidthAndHeight(decoration.side);

	//Calculat Left based on artboard width, decoration width and position center offset
	const leftMM = width / 2 + (decoration.centreOffset - decoration.width / 2);

	return {
		width: percentageOf(decoration.width, width),
		height: percentageOf(decoration.height, height),
		centreOffset: percentageOf(decoration.centreOffset, width),
		left: percentageOf(leftMM, width),
		top: percentageOf(decoration.top, height),
	};
}

export function getDecorationPositionAsPercentageOfProduct(side: GQLProductSideFragment, position: Position): Position {
	const { width, height, left, top } = getArtboardPositionAsPercentage(side);
	const decoration = getDecorationPositionAsPercentage({ ...position, side });

	//The decoration position is a percentge of the artboard which is then a percentage of the product
	//We need to convert the decoration position to a percentage of the product
	return {
		width: (decoration.width * width) / 100,
		height: (decoration.height * height) / 100,
		centreOffset: (decoration.centreOffset * width) / 100,
		left: (decoration.left * width) / 100 + left,
		top: (decoration.top * height) / 100 + top,
	};
}
